import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { RoleGuard } from './core/auth/guards/role.guard';
import { TunstallEmergencyListComponent } from './modules/admin/overview/emergency/tunstall-emergency-list/tunstall-emergency-list.component';
import { EmergencyAgentAccessComponent } from './modules/admin/overview/emergency/emergency-agent-access/emergency-agent-access.component';
import { UrlShortenedEaaComponent } from './modules/admin/url-shortened/url-shortened.component';
import { WifiSettingsComponent } from './modules/admin/settings/wifi-settings/wifi-settings.component';
import { AddUserComponent } from './modules/admin/user-table/add-user/add-user.component';
import { ExportCsvOfImeiComponent } from './modules/admin/export-csv-of-imei/export-csv-of-imei.component';
import { PncCentralCallComponent } from './modules/admin/pnc-central-call/pnc-central-call.component';
import { gmResolver } from './modules/shared/services/google-maps.service';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/dashboards/project'
    { path: '', pathMatch: 'full', redirectTo: 'configuration-user/personal' },

    // Redirect signed-in user to the '/dashboards/project'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: 'configuration-user/personal',
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.routes'),
            }
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        resolve: {
            initialData: gmResolver,
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.routes'),
            },
            {
                path: 'tunstall-emergency-list',
                component: TunstallEmergencyListComponent,
            },
            {
                path: 'emergency-center-agent-access',
                component: EmergencyAgentAccessComponent,
            },
            //  https://adele.test.almaphil.de/pnc-central-call/{profileId}/external-id/{userExternalId}
            {
                path: 'pnc-central-call/:profileId/external-id/:userExternalId',
                component: PncCentralCallComponent,
            },
            {
                path: 'user-health-stats',
                loadChildren: () =>
                    import(
                        'app/modules/admin/user-health-stats/user-health-stats.routes'
                    ),
            },
            { path: 's/:code', component: UrlShortenedEaaComponent },
            { path: 'ext/wifi-setting', component: WifiSettingsComponent },
            {
                path: 'public/support',
                children: [
                    {
                        path: 'help-center',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/help-center/help-center.routes'
                            ),
                    },
                ],
            },
        ],
    },

    // Onboarding routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'onboarding',
                loadChildren: () =>
                            import(
                                'app/modules/admin/user-config-flow/user-config-flow.routes'
                            ),
            },
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
            data: gmResolver,
        },
        children: [
            {
                path: 'overview',
                children: [
                    {
                        path: 'emergency',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/overview/emergency/emergency.routes'
                            ),
                    },
                    {
                        path: 'user-health-stats',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/user-health-stats/user-health-stats.routes'
                            ),
                    },
                    {
                        path: 'chat',
                        loadChildren: () =>
                            import('app/modules/admin/apps/chat/chat.routes'),
                    },
                    {
                        path: 'task',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/task-list/task-list.routes'
                            ),
                    },
                    {
                        path: 'calendar',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/calendar/calendar.routes'
                            ),
                    },
                ],
            },

            {
                path: 'configuration-user',
                children: [
                    {
                        path: 'personal',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/personal-settings/personal/personal.routes'
                            ),
                    },
                    {
                        path: 'health',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/personal-settings/health/health.routes'
                            ),
                    },
                    {
                        path: 'contacts',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/personal-settings/contacts/contacts.routes'
                            ),
                    },
                    // {
                    //     path: 'my-caregiver',
                    //     loadChildren: () =>
                    //         import(
                    //             'app/modules/admin/personal-settings/contacts/contacts.routes'
                    //         ),
                    // },
                    {
                        path: 'create-new-user',
                        component: AddUserComponent,
                    },
                    {
                        path: 'account',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/settings/settings.routes'
                            ),
                    },
                ],
            },

            {
                path: 'service-settings',
                children: [
                    {
                        path: 'emergency-call',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/service-settings/emergency-call/emergency-call.routes'
                            ),
                    },
                    {
                        path: 'automatic-emergency',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/service-settings/automatic-emergency/automatic-emergency.routes'
                            ),
                    },
                    {
                        path: 'geo-protection',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/service-settings/local-orientation/local-orientation.route'
                            ),
                    },
                    {
                        path: 'drinking-plan',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/service-settings/drinking-plan/drinking-plan.routes'
                            ),
                    },
                    {
                        path: 'exercise-plan',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/service-settings/exercise-plan/exercise-plan.route'
                            ),
                    },
                    {
                        path: 'general-reminder',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/service-settings/general-reminder/general-reminder.routes'
                            ),
                    },
                    {
                        path: 'questionnaire',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/service-settings/questionnaire/questionnaire.routes'
                            ),
                        data: { key: 'QUESTIONNAIRE' },
                    },
                    {
                        path: 'exercises',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/service-settings/questionnaire/questionnaire.routes'
                            ),
                        data: { key: 'EXERCISE' },
                    },
                    {
                        path: 'knowledge',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/service-settings/questionnaire/questionnaire.routes'
                            ),
                        data: { key: 'KNOWLEDGE' },
                    },
                    {
                        path: 'routine-plan',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/service-settings/routine-plan/routine-plan.routes'
                            ),
                    },
                    {
                        path: 'medication-plan',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/service-settings/medication-plan/medication-plan.routes'
                            ),
                    },
                    {
                        path: 'notes',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/service-settings/notes/notes.routes'
                            ),
                    },
                ],
            },
            {
                path: 'device-configuration',
                loadChildren: () =>
                    import(
                        'app/modules/admin/device-configuration/device-configuration.routes'
                    ),
            },
            {
                path: 'worth-knowing',
                children: [
                    {
                        path: 'AP_AP',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/worth-knowing/ap/ap.routes'
                            ),
                        data: { key: 'AP_AP' },
                    },
                    {
                        path: 'AP_CE',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/worth-knowing/ap/ap.routes'
                            ),
                        data: { key: 'AP_CE' },
                    },
                    {
                        path: 'AP_Manual',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/worth-knowing/ap/ap.routes'
                            ),
                        data: { key: 'AP_Manual' },
                    },
                    {
                        path: 'AP_compatibility',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/worth-knowing/ap/ap.routes'
                            ),
                        data: { key: 'AP_compatibility' },
                    },
                    {
                        path: 'AP_DS_web',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/worth-knowing/ap/ap.routes'
                            ),
                        data: { key: 'AP_DS_web' },
                    },
                    {
                        path: 'AP_NB',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/worth-knowing/ap/ap.routes'
                            ),
                        data: { key: 'AP_NB' },
                    },
                    {
                        path: 'AP_Impressum',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/worth-knowing/ap/ap.routes'
                            ),
                        data: { key: 'AP_Impressum' },
                    },
                    {
                        path: 'AP_FAQ',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/worth-knowing/ap/ap.routes'
                            ),
                        data: { key: 'AP_FAQ' },
                    },
                    // {
                    //     path: 'emergency-call',
                    //     loadChildren: () =>
                    //         import(
                    //             'app/modules/admin/service-settings/emergency-call/emergency-call-routes'
                    //         ),
                    // }
                ],
            },
            {
                path: 'support',
                children: [
                    {
                        path: 'help-center',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/help-center/help-center.routes'
                            ),
                    },
                ],
            },

            {
                path: 'admin',
                canActivate: [RoleGuard],
                data: { roles: ['ROLE_ADMIN'] },
                children: [
                    {
                        path: 'watch-table',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/watch-table/watch-table.routes'
                            ),
                    },
                    {
                        path: 'user-table',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/user-table/user-table.routes'
                            ),
                    },
                    /*{
                        path: 'esim-knox',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/esim-knox/esim-knox.routes'
                                ),
                    },
                    {
                        path: 'shipping',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/shipping/shipping.routes'
                                ),
                    },*/
                    {
                        path: 'device-insights',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/device-insights/device-insights.routes'
                            ),
                    },
                    {
                        path: 'export-csv',
                        component: ExportCsvOfImeiComponent,
                        // loadChildren: () =>
                        //     import(
                        //         'app/modules/admin/export-csv/export-csv.routes'
                        //     ),
                    },
                ],
            },

            // 404 & Catch all
            {
                path: '404-not-found',
                pathMatch: 'full',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/error/error-404/error-404.routes'
                    ),
            },
            { path: '**', redirectTo: '404-not-found' },
        ],
    },
];
